import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Hidden,
  Grid,
  Link,
  Typography
} from "@mui/material";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ExitToApp, Launch, Place } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import {
  buildCourseBuilderContentsRoute,
  buildCourseDetailsSessionsRoute
} from "../../../../routes/AdminRoutes";
import CourseOutline from "../../../../components/CourseOutline/CourseOutline";
import {
  courseLocationTypes,
  contentHosts,
  trainingObjectStatusIds
} from "../../../../constants";
import {
  updateCourse,
  fetchCustomerIltDetails
} from "../../../../redux/actions/contentCoursesActions";
import { getDurationString } from "../../../../utils/datetime";
import IltSessionForm from "../../../../components/Forms/IltSessionForm";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { filesPreloading } from "../../../../services/fileUploadService";
import Modal from "../../../../components/Modal";
import CourseTypeIcon from "../../../../components/Icons/CourseTypeIcon";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import { replaceTimeZone, formatDate } from "../../../../utils/datetime";
import { buildCoursePreviewRoute } from "../../../../routes/Routes";
import { useCourseSections } from "../../../../hooks/courses";

const selector = makeGetEntities();

const ZoomMeetingNotScheduledModal = ({ open, close }) => {
  const { t } = useTranslation();
  return (
    <Modal
      scrollable
      maxWidth="sm"
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("courseSession.zoomMeetingNotFoundTitle")}
        </Typography>
      }
      dialogContent={
        <Box display="flex" flexDirection="column">
          <Typography color="textPrimary">
            {t("courseSession.zoomMeetingNotFoundDescription")}
          </Typography>
        </Box>
      }
      dialogActions={
        <Button color="primary" onClick={close}>
          {t("courseSession.zoomMeetingNotFoundOk")}
        </Button>
      }
      open={open}
      showCloseIcon
    />
  );
};

const CourseSessionButtons = ({
  onPreviewClick,
  onManageInstructorsClick,
  onManageLocationsClick,
  onGoToCourseBuilderClick,
  onSaveClick,
  saveButtonDisabled,
  iltZoomMeetingStartUrl
}) => {
  const { t } = useTranslation();
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      mb={3.5}
      flexDirection={{ xs: "column", md: "row" }}
      flexWrap="wrap"
      alignItems="flex-start"
    >
      <ButtonGroup
        flexDirection={{ xs: "column", md: "row" }}
        alignItems="flex-start"
      >
        {onPreviewClick && (
          <Button
            startIcon={<Launch />}
            onClick={onPreviewClick}
            color="primary"
            size="large"
          >
            {t("courseVersion.details.previewBtn")}
          </Button>
        )}
        {onManageInstructorsClick && (
          <Button
            startIcon={<CourseTypeIcon type="ilt" color="inherit" />}
            onClick={onManageInstructorsClick}
            color="primary"
            size="large"
          >
            {t("courseSession.manageInstructorsBtn")}
          </Button>
        )}
        {onManageLocationsClick && (
          <Button
            startIcon={<Place />}
            onClick={onManageLocationsClick}
            color="primary"
            size="large"
          >
            {t("courseSession.manageLocationsBtn")}
          </Button>
        )}
      </ButtonGroup>
      <ButtonGroup alignItems="flex-start">
        {iltZoomMeetingStartUrl && (
          <Link href={iltZoomMeetingStartUrl} target="_blank">
            <Button color="primary" size="large">
              {t("courseSession.zoomMeetingStartBtn")}
            </Button>
          </Link>
        )}
        <Button
          startIcon={<ExitToApp />}
          variant="outlined"
          onClick={onGoToCourseBuilderClick}
          color="primary"
        >
          {t("courseVersion.details.courseBuilderBtn")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onSaveClick()}
          disabled={saveButtonDisabled}
        >
          {t("courseSession.saveChangesBtn")}
        </Button>
      </ButtonGroup>
    </Box>
  );
};

CourseSessionButtons.propTypes = {
  onGoToCourseBuilderClick: PropTypes.func
};

const CourseSessionDetails = ({
  sessionData,
  match: {
    params: { courseId, sessionId }
  }
}) => {
  const dispatch = useDispatch();
  const [zoomMeetingErrorOpen, setSoomMeetingErrorOpen] = React.useState(
    sessionData.iltZoomMeetingNotFound
  );
  const { t } = useTranslation();
  const [overviewFiles, setOverviewFiles] = useState([]);

  const { customerIltDetails, isUpdatingCourse } = useSelector(
    ({ contentCoursesState }) => {
      return {
        customerIltDetails: selector(contentCoursesState, {
          type: "customerIltDetails"
        })[0],
        isUpdatingCourse: contentCoursesState.isUpdatingCourse
      };
    }
  );

  useEffect(() => {
    if (!customerIltDetails) {
      dispatch(fetchCustomerIltDetails(courseId));
    }
  }, []);

  const handleGoToCourseBuilderClick = () => {
    dispatch(push(buildCourseBuilderContentsRoute(courseId, sessionId)));
  };

  const redirectToSessions = () => {
    dispatch(push(buildCourseDetailsSessionsRoute(courseId)));
  };

  const {
    statusTypeId,
    displayInLibrary,
    hideFromLibraryAfter,
    iltSeats,
    iltInstructor,
    iltLocationType,
    startDate,
    endDate,
    timeZone,
    trainingHours,
    name,
    description,
    virtualClassroomId,
    hasActiveAssignments,
    trainingLocationId,
    showOverview,
    overviewText
  } = sessionData;

  const {
    fetchCourseSections,
    mandatorySections,
    optionalSections
  } = useCourseSections(sessionId);

  const handleResponseError = (error, instructor) => {
    const messages = {};
    const vcRegExp = /^Virtual [cC]lassroom.*is unavailable during the date\/time selected/;
    if (error.errors) {
      error.errors.forEach(errorMsgObject => {
        if (parseInt(errorMsgObject.status) === 422) {
          if (errorMsgObject.detail.match(vcRegExp)) {
            let vcName = errorMsgObject.detail.match(/".*"/);
            if (vcName) {
              vcName = vcName[0].slice(1, -1);
            }
            const sessionName = errorMsgObject.detail.substring(
              errorMsgObject.detail.indexOf('The ILT "') + 'The ILT "'.length,
              errorMsgObject.detail.indexOf('" was previously scheduled')
            );
            messages.vc = t("scheduleSession.error.virtualClassroom", {
              virtualClassroomName: vcName,
              iltName: sessionName
            });
          } else {
            const iltInstructorRegExp = /^Instructor.* was previously scheduled for this instructor at this date and time/;
            const iltLocationRegExp = /^Location.* was previously scheduled for this location at this date and time/;
            if (errorMsgObject.detail.match(iltInstructorRegExp)) {
              const sessionName = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf("Name:") + "Name:".length + 1,
                errorMsgObject.detail.length
              );
              console.log("sessionName", sessionName);
              messages.generic = t("scheduleSession.error.instructor", {
                instructor: instructor,
                iltName: sessionName
              });
            } else if (errorMsgObject.detail.match(iltLocationRegExp)) {
              const scheduledTime = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf("Time:") + "Time:".length + 1,
                errorMsgObject.detail.length
              );
              const sessionName = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf("Name:") + "Name:".length + 1,
                errorMsgObject.detail.indexOf("Time:") - 1
              );
              messages.generic = t("scheduleSession.error.location", {
                iltName: sessionName,
                scheduledTime: scheduledTime
              });
            } else {
              messages.generic = t("scheduleSession.error.generic");
            }
          }
        }
      });
    }
    Object.values(messages).forEach(message =>
      dispatch(enqueueErrorSnackbar({ message: message }))
    );
  };

  const deactivated = (oldStatus, newStatus) =>
    parseInt(oldStatus) === trainingObjectStatusIds.active &&
    parseInt(newStatus) === trainingObjectStatusIds.inactive;

  const handlePreviewCourse = () => {
    window.open(buildCoursePreviewRoute(sessionId));
  };

  const initialValues = useMemo(() => {
    return {
      statusTypeId,
      displayInLibrary: `${displayInLibrary}`,
      hideFromLibraryAfter: hideFromLibraryAfter,
      locationType: iltLocationType ? iltLocationType : "",
      seats: iltSeats ? iltSeats : "",
      locationId: trainingLocationId ? trainingLocationId : "",
      virtualClassroomId: virtualClassroomId ? virtualClassroomId : "",
      instructor: iltInstructor ? iltInstructor.id : "",
      startTime: replaceTimeZone(startDate),
      endTime: replaceTimeZone(endDate),
      startDate: replaceTimeZone(startDate),
      endDate: replaceTimeZone(endDate),
      timeZone: timeZone ? timeZone : "",
      hasActiveAssignments: hasActiveAssignments ? hasActiveAssignments : null,
      showOverview: !!showOverview,
      overviewText,
      overviewTextOnlyLength: 0,
      overviewMedia: {}
    };
  }, [sessionData.id]);

  return (
    <IltSessionForm
      initialValues={initialValues}
      disabled={isUpdatingCourse}
      trainingHours={getDurationString(trainingHours, t)}
      iltName={name}
      description={description}
      trainingLocationId={trainingLocationId}
      locations={customerIltDetails ? customerIltDetails.trainingLocations : []}
      virtualClassrooms={
        customerIltDetails ? customerIltDetails.virtualClassrooms : []
      }
      instructors={customerIltDetails ? customerIltDetails.instructors : []}
      virtualClassroomAllowed={
        customerIltDetails ? customerIltDetails.virtualClassroomAllowed : []
      }
      onSubmit={async (values, { setSubmitting }) => {
        delete values.overviewTextOnlyLength;
        const tmpObj = await filesPreloading(
          overviewFiles,
          values.overviewText
        );
        const valuesCopy = { ...values };
        valuesCopy.overviewMedia.images = tmpObj.images;
        valuesCopy.overviewMedia.media = tmpObj.media;
        valuesCopy.overviewMedia.attachments = tmpObj.attachments;
        valuesCopy.overviewMedia.content = tmpObj.content;
        valuesCopy.startDate = formatDate(values.startDate, "YYYY-MM-DD");
        valuesCopy.endDate = formatDate(values.endDate, "YYYY-MM-DD");
        valuesCopy.startTime = formatDate(values.startTime, "h:mm A");
        valuesCopy.endTime = formatDate(values.endTime, "h:mm A");
        /**
         * We should probably update this to send the instructor
         * as a relationship instead of just sending the ID
         * **What for, though? For the backend purposes ID is enough.
         */
        dispatch(
          updateCourse(
            sessionId,
            {
              ...sessionData,
              ...valuesCopy,
              instructor: values.instructor.id,
              statusTypeId: parseInt(values.statusTypeId)
            },
            () => {
              if (deactivated(statusTypeId, values.statusTypeId)) {
                redirectToSessions();
              } else {
                fetchCourseSections();
              }
              setSubmitting(false);
            },
            error => handleResponseError(error, valuesCopy.instructor.text)
          )
        );
      }}
      render={({
        formFields,
        formikProps: { submitForm, values, isSubmitting, setFieldValue, errors }
      }) => (
        <Box display="flex" flexDirection="column" pt={3}>
          <CourseSessionButtons
            onPreviewClick={handlePreviewCourse}
            onGoToCourseBuilderClick={handleGoToCourseBuilderClick}
            iltZoomMeetingStartUrl={sessionData.iltZoomMeetingStartUrl}
            saveButtonDisabled={isSubmitting || isUpdatingCourse}
            onSaveClick={submitForm}
          />
          <ZoomMeetingNotScheduledModal
            open={zoomMeetingErrorOpen}
            close={() => {
              setSoomMeetingErrorOpen(false);
            }}
          />
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
            <Box display="flex" flexDirection="column" flex={3} mb={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box mb={3}>{formFields.name}</Box>
                  <Box mb={3}>{formFields.description}</Box>
                  <Box mb={3}>{formFields.trainingHours}</Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mb={4}>
                    <Box mb={3}>{formFields.isActive}</Box>
                    <Box mb={3}>{formFields.displayInLibrary}</Box>
                    {formFields.hideFromLibraryAfter}
                  </Box>
                </Grid>
              </Grid>
              <Box my={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {formFields.locationType}
                  </Grid>
                  {values.locationType ===
                    courseLocationTypes.virtualClassroom && (
                    <Grid item xs={12}>
                      {formFields.virtualClassroom}
                    </Grid>
                  )}
                  {values.locationType === courseLocationTypes.inPerson && (
                    <Grid item xs={12}>
                      {formFields.location}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    {formFields.capacity}
                  </Grid>
                  <Grid item xs={12}>
                    {formFields.instructor}
                  </Grid>
                  <Grid item xs={12}>
                    {formFields.startDate}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {formFields.startTime}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {formFields.timeZone}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {formFields.endDate}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {formFields.endTime}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Hidden smDown>
              <Divider
                orientation="vertical"
                sx={{ height: "unset", my: 0, mx: 4 }}
              />
            </Hidden>
            <Box flex={5}>
              <CourseOutline
                courseContent={mandatorySections}
                resources={optionalSections}
                hideOverviewCheckbox={
                  sessionData.contentHost !== contentHosts.visionHosted
                }
                overviewCheckboxDisabled={
                  values.locationType === courseLocationTypes.virtualClassroom
                }
                onFilesListChange={filesList => {
                  setOverviewFiles(filesList);
                }}
                filesList={overviewFiles}
                disableFields={isUpdatingCourse}
              />
            </Box>
          </Box>
        </Box>
      )}
    />
  );
};

export default CourseSessionDetails;

CourseSessionDetails.propTypes = {
  sessionData: PropTypes.shape({
    seats: PropTypes.number,
    trainingLocation: PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      seats: PropTypes.number
    }),
    instructor: PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string
    }),
    statusTypeId: PropTypes.oneOf(Object.values(trainingObjectStatusIds)),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    timeZone: PropTypes.string,
    locationType: PropTypes.string,
    trainingHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayInLibrary: PropTypes.bool,
    hideFromLibraryAfter: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    courseSections: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        id: PropTypes.string,
        title: PropTypes.string,
        position: PropTypes.number,
        shortCourseActivities: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            id: PropTypes.string,
            title: PropTypes.string,
            contentableType: PropTypes.string,
            position: PropTypes.number
          })
        )
      })
    )
  })
};
